import { Luxon } from '@helpers/luxon';
import { AsyncPipe } from '@angular/common';
import { HttpContext } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, OnDestroy, OnInit, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { EMPTY, Subscription, catchError, filter, map, switchMap, timer } from 'rxjs';
import { ApiEndpointService } from '@core/services/api/api-endpoint.service';
import { IGNORAR_ERRO_GLOBAL } from '@core/services/api/interceptor';
import { AuthService } from '@core/services/common/auth.service';
import { segundoFatorSatisfeito } from '@model/two-factor-current-method.model';
import { SubscriptionCollection, SubscriptionCollectionService } from '@core/services/subscription-collection.service';

@Component({
  selector: 'mg-subscription-notice',
  imports: [AsyncPipe, RouterLink],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (state.collection | async; as collection) {
      @if (collection.stage !== 'current') {
        <aside class="collection-notice" [class.collection-notice--warning]="collection.severity === 'warning'" [class.collection-notice--danger]="collection.severity === 'danger'" role="status">
          @if (collection.message_before !== undefined) {
            <span data-testid="mensagem-cobranca">
              @if (collection.action_label) {
                {{ collection.message_before }}
                @if (naAssinatura()) {
                  <a href="#pagamentos" data-testid="acao-cobranca" (click)="irParaPagamentos($event)">{{ collection.action_label }}</a>
                } @else {
                  <a routerLink="/painel/configuracoes/assinatura" data-testid="acao-cobranca">{{ collection.action_label }}</a>
                }
                {{ collection.message_after }}
              } @else {
                {{ collection.message }}
              }
            </span>
          } @else {
            <span data-testid="mensagem-cobranca">
              @if (naAssinatura()) {
                <a href="#pagamentos" (click)="irParaPagamentos($event)">{{ collection.message }}</a>
              } @else {
                {{ collection.message }}
              }
              @if (collection.stage === 'hard' && collection.original_due_at) {
                <strong>Suspensão do acesso em {{ dataDaSuspensao(collection.original_due_at) }} se o pagamento não for confirmado.</strong>
              }
            </span>
            @if (!naAssinatura()) {
              <a routerLink="/painel/configuracoes/assinatura">{{ collection.stage === 'inactive' ? 'Consultar assinatura' : 'Regularizar assinatura' }}</a>
            }
          }
        </aside>
      }
    }
  `,
  styles: [`
    .collection-notice { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: .75rem;
      padding: 1rem 1.5rem; background: var(--mg-info-light); color: var(--mg-text); border-bottom: 2px solid var(--mg-primary); }
    .collection-notice--warning { background: var(--mg-warning-light); border-color: var(--mg-warning); }
    strong { display: block; }
    .collection-notice--danger { background: var(--mg-danger-light); border-color: var(--mg-danger); }
    a { color: var(--mg-primary); font-weight: 600; text-decoration: underline; }
  `],
})
export class SubscriptionNoticeComponent implements OnInit, OnDestroy {
  protected readonly state = inject(SubscriptionCollectionService);
  protected readonly router = inject(Router);
  /** Sinal de navegação: o template OnPush só reavalia a rota quando um sinal muda. */
  protected readonly urlAtual = toSignal(
    this.router.events.pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd), map(e => e.urlAfterRedirects)),
    { initialValue: this.router.url },
  );
  protected readonly naAssinatura = computed(() => this.urlAtual().startsWith('/painel/configuracoes/assinatura'));
  private readonly api = inject(ApiEndpointService);
  private readonly auth = inject(AuthService);
  private subscription?: Subscription;

  /** Rola até a tabela de pagamentos da própria tela e leva o foco para ela. */
  protected irParaPagamentos(evento: Event): void {
    evento.preventDefault();
    const secao = document.getElementById('pagamentos');
    secao?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    secao?.focus({ preventScroll: true });
  }

  dataDaSuspensao(dueAt: string): string {
    return Luxon.noFusoDoTenant(dueAt).plus({ days: 14 }).toFormat('dd/MM/yyyy');
  }

  ngOnInit(): void {
    this.subscription = this.auth.twoFactorMethodObserver.pipe(
      switchMap(method => {
        if (!segundoFatorSatisfeito(method)) {
          this.state.collection.next(null);
          return EMPTY;
        }
        return timer(0, 5 * 60 * 1000).pipe(
          switchMap(() => this.api.getHttp().get<{ data: { collection?: SubscriptionCollection } | null }>(
            `${this.api.getApiUrl()}/assinatura`, { context: new HttpContext().set(IGNORAR_ERRO_GLOBAL, true) },
          ).pipe(catchError(() => EMPTY))),
        );
      }),
    ).subscribe(response => this.state.collection.next(response.data?.collection ?? null));
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.state.collection.next(null);
  }
}
